// Keeps a bounded in-memory copy of recent console output so that failure
// telemetry (see loginTelemetry.ts) can attach the log context that preceded
// an error. Installed as a module side effect via the second import of
// src/entrypoints/index.tsx, before the rest of the module graph evaluates.

type ConsoleLevel = 'debug' | 'log' | 'info' | 'warn' | 'error'

interface ConsoleBufferEntry {
    t: string
    level: ConsoleLevel
    msg: string
}

const MAX_ENTRIES = 100
const MAX_ENTRY_CHARS = 500
const MAX_STACK_CHARS = 500

const buffer: ConsoleBufferEntry[] = []
let installed = false

function formatArg(arg: unknown): string {
    if (typeof arg === 'string') {
        return arg
    }
    if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}\n${(arg.stack ?? '').substring(0, MAX_STACK_CHARS)}`
    }
    try {
        return JSON.stringify(arg)
    } catch {
        return String(arg)
    }
}

function push(level: ConsoleLevel, args: unknown[]): void {
    try {
        const msg = args.map(formatArg).join(' ').substring(0, MAX_ENTRY_CHARS)
        buffer.push({ t: new Date().toISOString(), level, msg })
        if (buffer.length > MAX_ENTRIES) {
            buffer.shift()
        }
    } catch {
        // The buffer must never break logging itself.
    }
}

export function installConsoleBuffer(): void {
    if (installed) {
        return
    }
    installed = true
    const levels: ConsoleLevel[] = ['debug', 'log', 'info', 'warn', 'error']
    for (const level of levels) {
        const original = console[level].bind(console)
        console[level] = (...args: unknown[]) => {
            push(level, args)
            original(...args)
        }
    }
}

/**
 * Recent console output, oldest first. When the joined text exceeds maxChars
 * the oldest lines are dropped; 8000 stays under the Application Insights
 * 8192-character custom property cap.
 */
export function getConsoleBufferSnapshot(maxChars = 8000): string {
    const lines = buffer.map(entry => `${entry.t} [${entry.level}] ${entry.msg}`)
    let snapshot = lines.join('\n')
    while (lines.length > 1 && snapshot.length > maxChars) {
        lines.shift()
        snapshot = `…[truncated]\n${lines.join('\n')}`
    }
    return snapshot.substring(0, maxChars)
}

export function clearConsoleBuffer(): void {
    buffer.length = 0
}

installConsoleBuffer()
